<template>
  <div>
    <hr class="short">

    <div class="row gx-2">
      <div class="mb-3 col-sm-6">
        <label
          for="activity-id"
          class="form-label required"
        >
          {{ $t('project_based_application.activity') }}
        </label>
        <select
          id="activity-id"
          v-model="selectedActivityId"
          class="form-select"
        >
          <option :value="null" />
          <option
            v-for="activity of activities"
            :key="activity.id"
            :value="activity.id"
          >
            {{ activity.name }}
          </option>
        </select>
      </div>

      <div class="mb-3 col-sm-6">
        <label
          for="function-id"
          class="form-label required"
        >
          {{ $t('project_based_application.function') }}
        </label>
        <select
          id="function-id"
          v-model="applicationData.functionId"
          class="form-select"
          :disabled="!selectFunctionDisabled"
        >
          <option :value="null" />
          <option
            v-for="aFunction of functions"
            :key="aFunction.id"
            :value="aFunction.id"
          >
            {{ aFunction.name }}
          </option>
        </select>
      </div>
    </div>

    <template v-if="selectedActivity">
      <hr class="short">
      <h4>{{ $t('project_based_application.instructions') }}</h4>
      <div
        v-if="hasInstructions"
        v-html="selectedActivity.instructionsHtml"
      />

      <div
        v-for="file of files"
        :key="file.url"
      >
        <a
          :href="file.url"
          target="_blank"
        >
          {{ file.fileName }}
        </a>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'

import type {
  OspeFunction,
  OspeProjectBasedActivity,
  OspeProjectBasedActivityTemplateFile,
  ProjectBasedApplication
} from '../types'
import {isNonEmptyString} from '@/js/js-utils'

const activities = ref<OspeProjectBasedActivity[]>([])
const selectedActivity = ref<OspeProjectBasedActivity | null>(null)
const applicationData = ref<ProjectBasedApplication>({
  activityId: null,
  functionId: null,
})
const applicationDataEl = ref<HTMLInputElement | null>(null)

const selectedActivityId = computed<number | null>({
  get() {
    return selectedActivity.value?.id ?? null
  },
  set(newId: number | null): void {
    const activity = activities.value.find(item => item.id == newId)
    if (activity) {
      applicationData.value.activityId = activity.id
      selectedActivity.value = activity
    } else {
      applicationData.value.activityId = null
      selectedActivity.value = null
    }
  },
})

const selectFunctionDisabled = computed(() => selectedActivity.value != null)

const functions = computed<OspeFunction[]>(() => {
  if (Array.isArray(selectedActivity.value?.functions)) {
    return selectedActivity.value.functions
  }
  return []
})

const hasInstructions = computed(() => {
    return isNonEmptyString(selectedActivity.value?.instructionsHtml)
})

const files = computed<OspeProjectBasedActivityTemplateFile[]>(() => {
  if (Array.isArray(selectedActivity.value?.templateFiles)) {
    return selectedActivity.value.templateFiles
  }

  return []
})

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(applicationData.value)
  }
}

watch(
  applicationData,
  () => {
    storeApplicationData()
  },
  {deep: true}
)

watch(
  () => applicationData.value.activityId,
  () => {
    let newFunctionId: number | null = null
    const aFunction = functions.value.find(func => func.id == applicationData.value.functionId)
    if (aFunction) {
      newFunctionId = aFunction.id
    }
    applicationData.value.functionId = newFunctionId
  }
)

onMounted(() => {
  const exchangesDataEl = document.querySelector('script[ref="exchangeSelectionData"]')
  if (exchangesDataEl instanceof HTMLScriptElement && exchangesDataEl.type == 'application/json') {
    try {
      const exchangeData = JSON.parse(exchangesDataEl.innerText)
      if (Array.isArray(exchangeData.activities)) {
        activities.value = exchangeData.activities
      }
    }
    catch { /* empty */ }
  }
  const appDataEl = document.querySelector('input[ref="applicationDataJson"]')
  if (appDataEl instanceof HTMLInputElement) {
    applicationDataEl.value = appDataEl
    try {
      applicationData.value = JSON.parse(applicationDataEl.value.value)
      selectedActivityId.value = applicationData.value.activityId
    } catch { /* empty */ }
  }
})
</script>
